import React, { useState, useEffect, useMemo } from "react";
import {
  Table, Input, Button, Drawer, Form, Select, Switch,
  DatePicker, Tooltip, message, Avatar,
} from "antd";
import {
  SearchOutlined, PlusOutlined, EditOutlined, FileTextOutlined, FileExcelOutlined,
  TeamOutlined, UserOutlined, HomeOutlined, SolutionOutlined, ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { apiRequest } from "../../../utilities/apiMethod";
import { useThemeStore } from "../../../store/useThemeStore";
import { colSearch, StatusTag, actionBtnStyle, exportCSV, exportExcel } from "../masterUtils";
import { formatantdayjsDate } from "../../../utilities/utll";

/* ── small helpers ───────────────────────────────────────────────────────── */
const GENDER_OPTS  = [{ value: "Male", label: "Male" }, { value: "Female", label: "Female" }, { value: "Other", label: "Other" }];
const INPUT_H      = { style: { borderRadius: 8, height: 40 } };
const SELECT_H     = { style: { borderRadius: 8, height: 40 }, popupMatchSelectWidth: false };

function SectionHead({ icon, title }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "20px 0 14px" }}>
      <div style={{ width: 28, height: 28, borderRadius: 7, background: "#f4f5f7", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {icon}
      </div>
      <span style={{ fontSize: 12, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.07em" }}>{title}</span>
      <div style={{ flex: 1, height: 1, background: "#f0f1f3", marginLeft: 4 }} />
    </div>
  );
}

function FieldLabel({ children, required }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6 }}>
      {children}{required && <span style={{ color: "#ef4444", marginLeft: 2 }}>*</span>}
    </div>
  );
}

function EmpAvatar({ firstName, lastName, size = 26 }) {
  const initials = ((firstName?.[0] ?? "") + (lastName?.[0] ?? "")).toUpperCase() || "?";
  const colors   = ["#6366f1","#8b5cf6","#ec4899","#f59e0b","#10b981","#3b82f6","#ef4444","#14b8a6"];
  const color    = colors[(firstName?.charCodeAt(0) ?? 0) % colors.length];
  return (
    <Avatar size={size} style={{ background: color, fontWeight: 700, fontSize: size * 0.38, flexShrink: 0 }}>
      {initials}
    </Avatar>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
export default function EmployeeMaster() {
  const { themeColor } = useThemeStore();

  /* lookup data */
  const [empTypes,     setEmpTypes]     = useState([]);
  const [departments,  setDepartments]  = useState([]);
  const [designations, setDesignations] = useState([]);
  const [countries,    setCountries]    = useState([]);
  const [states,       setStates]       = useState([]);
  const [cities,       setCities]       = useState([]);

  /* list data */
  const [data,         setData]         = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [search,       setSearch]       = useState("");
  const [pagination,   setPagination]   = useState({ current: 1, pageSize: 10 });

  /* drawer */
  const [open,    setOpen]    = useState(false);
  const [editRec, setEditRec] = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [addrLoading, setAddrLoading] = useState(false);

  /* form */
  const [form] = Form.useForm();

  /* ── initial load ──────────────────────────────────────────────────────── */
  useEffect(() => {
    Promise.all([
      fetchLookup("Employee/employmenttypes", setEmpTypes),
      fetchLookup("Designation",              setDesignations),
      fetchLookup("Department",               setDepartments),
      fetchLookup("Country",                  setCountries),
      fetchLookup("State",                    setStates),
      fetchLookup("City",                     setCities),
    ]).then(fetchList);
  }, []);

  async function fetchLookup(endpoint, setter) {
    try {
      const res = await apiRequest("GET", endpoint);
      setter(Array.isArray(res?.Data) ? res.Data : []);
    } catch { setter([]); }
  }

  async function fetchList() {
    setLoading(true);
    try {
      const res = await apiRequest("GET", "Employee");
      setData(Array.isArray(res?.Data) ? res.Data : []);
    } finally { setLoading(false); }
  }

  /* ── filtered rows ─────────────────────────────────────────────────────── */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter(r =>
      r.EmployeeCode?.toLowerCase().includes(q) ||
      r.FirstName?.toLowerCase().includes(q) ||
      r.LastName?.toLowerCase().includes(q) ||
      r.Gender?.toLowerCase().includes(q)
    );
  }, [data, search]);

  /* ── name resolvers ────────────────────────────────────────────────────── */
  const deptName   = id => departments.find(d => d.ID === id)?.DepartmentName ?? "—";
  const desigName  = id => designations.find(d => d.ID === id)?.DesignationName ?? "—";
  const empTypeName= id => empTypes.find(e => e.EmploymentTypeID === id)?.Description ?? "—";

  /* ── drawer open / close ───────────────────────────────────────────────── */
  function openAdd() {
    setEditRec(null);
    form.resetFields();
    form.setFieldsValue({ isActive: false });
    setOpen(true);
  }

  async function openEdit(rec) {
    setEditRec(rec);
    setOpen(true);
    form.setFieldsValue({
      employeeCode:  rec.EmployeeCode,
      firstName:     rec.FirstName,
      lastName:      rec.LastName,
      gender:        rec.Gender,
      dob:           rec.DOB ? dayjs(rec.DOB) : null,
      doj:           rec.DOJ ? dayjs(rec.DOJ) : null,
      departmentID:  rec.DepartmentID,
      designationID: rec.DesignationID,
      employeeType:  rec.EmploymentTypeId === 0 ? null : rec.EmploymentTypeId,
      managerId:     rec.ManagerId === 0 ? null : rec.ManagerId,
      isActive:      rec.IsActive,
    });
    /* fetch address */
    setAddrLoading(true);
    try {
      const aRes = await apiRequest("GET", `EmployeeAddress/ByEmployeeId/${rec.ID}`);
      if (aRes?.IsSuccess && aRes.Data?.length > 0) {
        const a = aRes.Data[0];
        form.setFieldsValue({
          address1:  a.Address1  ?? "",
          address2:  a.Address2  ?? "",
          cityID:    a.CityID,
          stateId:   a.StateID,
          countryID: a.CountryID,
          pincode:   a.Pincode   ?? "",
        });
      }
    } finally { setAddrLoading(false); }
  }

  function closeDrawer() { setOpen(false); setSaving(false); }

  /* ── save ──────────────────────────────────────────────────────────────── */
  async function onFinish(values) {
    setSaving(true);
    try {
      const isEdit = !!editRec;
      const empPayload = {
        employeeCode:     values.employeeCode,
        firstName:        values.firstName,
        lastName:         values.lastName,
        gender:           values.gender,
        dob:              formatantdayjsDate(values.dob),
        doj:              formatantdayjsDate(values.doj),
        departmentID:     values.departmentID,
        designationID:    values.designationID,
        employmentTypeId: values.employeeType ?? 0,
        managerId:        values.managerId    ?? 0,
        isActive:         !!values.isActive,
      };

      const empRes = await apiRequest(
        isEdit ? "PUT" : "POST",
        isEdit ? `Employee/${editRec.ID}` : "Employee",
        empPayload
      );

      if (!empRes?.IsSuccess) {
        throw new Error(empRes?.displayMessage || (empRes?.ErrorMessage?.[0]) || "Failed to save employee");
      }

      const employeeId = empRes.Data?.ID ?? editRec?.ID;

      const addrPayload = {
        employeeID: employeeId,
        address1:   values.address1  ?? "",
        address2:   values.address2  ?? "",
        cityID:     values.cityID,
        stateID:    values.stateId,
        countryID:  values.countryID,
        pincode:    values.pincode   ?? "",
      };

      const addrRes = await apiRequest(
        isEdit ? "PUT" : "POST",
        isEdit ? `EmployeeAddress/${employeeId}` : "EmployeeAddress",
        addrPayload
      );

      if (!addrRes?.IsSuccess) {
        message.warning("Employee saved but address update failed.");
      } else {
        message.success(isEdit ? "Employee updated successfully." : "Employee created successfully.");
      }

      closeDrawer();
      fetchList();
    } catch (err) {
      message.error(err.message || "Something went wrong.");
    } finally {
      setSaving(false);
    }
  }

  /* ── columns ───────────────────────────────────────────────────────────── */
  const columns = [
    {
      title: "S.No", key: "sno", width: 64, align: "center",
      render: (_, __, i) => <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}>{(pagination.current - 1) * pagination.pageSize + i + 1}</span>,
    },
    {
      title: "Employee", key: "employee", width: 200,
      ...colSearch("EmployeeCode", "Employee Code", themeColor),
      render: (_, rec) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <EmpAvatar firstName={rec.FirstName} lastName={rec.LastName} />
          <div style={{ lineHeight: 1 }}>
            <div style={{ fontWeight: 600, color: "#111827", fontSize: 13 }}>
              {rec.FirstName} {rec.LastName}
            </div>
            <div style={{ fontFamily: "monospace", fontSize: 10.5, color: "#9ca3af", marginTop: 2 }}>
              {rec.EmployeeCode}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Gender", dataIndex: "Gender", key: "Gender", width: 100,
      filters: [{ text: "Male", value: "Male" }, { text: "Female", value: "Female" }, { text: "Other", value: "Other" }],
      onFilter: (val, rec) => rec.Gender === val,
      render: v => {
        const cfg = { Male: { color: "#dbeafe", text: "#1d4ed8" }, Female: { color: "#fce7f3", text: "#be185d" }, Other: { color: "#f3e8ff", text: "#7c3aed" } };
        const c = cfg[v] || { color: "#f3f4f6", text: "#374151" };
        return <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: c.color, color: c.text }}>{v || "—"}</span>;
      },
    },
    {
      title: "Department", dataIndex: "DepartmentID", key: "DepartmentID", width: 160,
      render: id => <span style={{ color: "#374151", fontSize: 13 }}>{deptName(id)}</span>,
    },
    {
      title: "Designation", dataIndex: "DesignationID", key: "DesignationID", width: 160,
      render: id => <span style={{ color: "#374151", fontSize: 13 }}>{desigName(id)}</span>,
    },
    {
      title: "Date of Joining", dataIndex: "DOJ", key: "DOJ", width: 140,
      render: v => <span style={{ fontSize: 12, color: "#6b7280" }}>{v ? dayjs(v).format("DD MMM YYYY") : "—"}</span>,
    },
    {
      title: "Status", dataIndex: "IsActive", key: "IsActive", width: 100, align: "center",
      filters: [{ text: "Active", value: true }, { text: "Inactive", value: false }],
      onFilter: (val, rec) => rec.IsActive === val,
      render: v => <StatusTag active={v} />,
    },
    {
      title: "Action", key: "action", width: 70, align: "center",
      render: (_, rec) => (
        <Tooltip title="Edit">
          <button
            onClick={() => openEdit(rec)}
            style={actionBtnStyle(themeColor)}
            onMouseEnter={e => Object.assign(e.currentTarget.style, { background: "#f0f4ff", borderColor: themeColor + "55" })}
            onMouseLeave={e => Object.assign(e.currentTarget.style, { background: "#f9fafb", borderColor: "#e5e7eb" })}
          >
            <EditOutlined style={{ fontSize: 14 }} />
          </button>
        </Tooltip>
      ),
    },
  ];

  /* ── export rows ───────────────────────────────────────────────────────── */
  const exportRows = filtered.map((r, i) => ({
    "S.No":           i + 1,
    "Employee Code":  r.EmployeeCode,
    "First Name":     r.FirstName,
    "Last Name":      r.LastName,
    "Gender":         r.Gender,
    "DOB":            r.DOB ? dayjs(r.DOB).format("DD-MMM-YYYY") : "",
    "DOJ":            r.DOJ ? dayjs(r.DOJ).format("DD-MMM-YYYY") : "",
    "Department":     deptName(r.DepartmentID),
    "Designation":    desigName(r.DesignationID),
    "Status":         r.IsActive ? "Active" : "Inactive",
  }));

  /* ── managers list (all employees except self) ─────────────────────────── */
  const managerOpts = useMemo(() =>
    data
      .filter(e => !editRec || e.ID !== editRec.ID)
      .map(e => ({ value: e.ID, label: `${e.FirstName} ${e.LastName} (${e.EmployeeCode})` })),
  [data, editRec]);

  /* ═══════════════════════════════════════════════════════════════════════ */
  return (
    <div style={{ padding: "4px 0" }}>

      {/* ── Page header ─────────────────────────────────────────────────── */}
      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #f0f1f3", boxShadow: "0 1px 6px rgba(0,0,0,0.04)", padding: "12px 18px", marginBottom: 10, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: themeColor, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 4px 14px ${themeColor}44`, flexShrink: 0, transition: "background 0.3s" }}>
            <TeamOutlined style={{ fontSize: 17, color: "#fff" }} />
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#111827" }}>Employee Master</h2>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 3 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: themeColor }}>{data.length}</span>
              <span style={{ fontSize: 13, color: "#6b7280" }}>Total</span>
              <span style={{ width: 1, height: 12, background: "#e5e7eb", display: "inline-block" }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: "#16a34a" }}>{data.filter(r => r.IsActive).length}</span>
              <span style={{ fontSize: 13, color: "#6b7280" }}>Active</span>
              <span style={{ width: 1, height: 12, background: "#e5e7eb", display: "inline-block" }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: "#dc2626" }}>{data.filter(r => !r.IsActive).length}</span>
              <span style={{ fontSize: 13, color: "#6b7280" }}>Inactive</span>
            </div>
          </div>
        </div>
        <Button
          icon={<PlusOutlined />} onClick={openAdd}
          style={{ height: 38, borderRadius: 9, fontWeight: 600, fontSize: 13, background: themeColor, borderColor: themeColor, color: "#fff", boxShadow: `0 2px 8px ${themeColor}44`, flexShrink: 0, transition: "background 0.3s" }}
        >
          Add Employee
        </Button>
      </div>

      {/* ── Table card ──────────────────────────────────────────────────── */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #f0f1f3", boxShadow: "0 1px 6px rgba(0,0,0,0.04)", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderBottom: "1px solid #f0f1f3", flexWrap: "wrap", gap: 8 }}>
          <Input
            placeholder="Search by name, code or gender…"
            prefix={<SearchOutlined style={{ color: "#c4c7cd" }} />}
            value={search}
            onChange={e => { setSearch(e.target.value); setPagination(p => ({ ...p, current: 1 })); }}
            allowClear
            style={{ width: 280, borderRadius: 8, height: 34 }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 13, color: "#6b7280" }}>{filtered.length} record{filtered.length !== 1 ? "s" : ""}</span>
            <Button icon={<FileTextOutlined />} onClick={() => exportCSV(exportRows, "employee_master.csv")} style={{ height: 32, borderRadius: 8, fontWeight: 600, borderColor: "#22c55e", color: "#16a34a", fontSize: 12 }}>CSV</Button>
            <Button icon={<FileExcelOutlined />} onClick={() => exportExcel(exportRows, "employee_master.xlsx")} style={{ height: 32, borderRadius: 8, fontWeight: 600, borderColor: "#16a34a", color: "#15803d", fontSize: 12 }}>Excel</Button>
          </div>
        </div>

        <Table
          rowKey="ID"
          columns={columns}
          dataSource={filtered}
          loading={loading}
          size="small"
          scroll={{ x: 1000 }}
          onChange={pag => setPagination({ current: pag.current, pageSize: pag.pageSize })}
          pagination={{
            ...pagination, total: filtered.length,
            showSizeChanger: true, pageSizeOptions: ["10", "20", "50"],
            showTotal: (t, r) => `Showing ${r[0]}–${r[1]} of ${t}`,
            style: { padding: "8px 14px", margin: 0 },
          }}
        />
      </div>

      {/* ── Add / Edit Drawer ────────────────────────────────────────────── */}
      <Drawer
        open={open}
        onClose={closeDrawer}
        width={620}
        destroyOnClose
        styles={{ body: { padding: "0 24px 24px", background: "#f9fafb" }, header: { background: "#fff", borderBottom: "1px solid #f0f1f3" } }}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 9, background: editRec ? "#f0f4ff" : themeColor, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {editRec
                ? <EditOutlined  style={{ fontSize: 16, color: themeColor }} />
                : <PlusOutlined  style={{ fontSize: 16, color: "#fff"      }} />
              }
            </div>
            <div>
              <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#111827", lineHeight: 1.2 }}>
                {editRec ? `Edit Employee` : "Add Employee"}
              </p>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: "#6b7280", fontWeight: 400 }}>
                {editRec ? `${editRec.FirstName} ${editRec.LastName} · ${editRec.EmployeeCode}` : "Fill in the details below"}
              </p>
            </div>
          </div>
        }
        footer={
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", padding: "4px 0" }}>
            <Button icon={<ReloadOutlined />} onClick={() => { form.resetFields(); form.setFieldsValue({ isActive: false }); }} style={{ height: 40, borderRadius: 8 }}>Reset</Button>
            <Button onClick={closeDrawer} style={{ height: 40, borderRadius: 8, minWidth: 90 }}>Cancel</Button>
            <Button
              htmlType="submit"
              form="emp-form"
              loading={saving}
              style={{ height: 40, borderRadius: 8, fontWeight: 700, background: themeColor, borderColor: themeColor, color: "#fff", minWidth: 140, transition: "background 0.3s" }}
            >
              {editRec ? "Save Changes" : "Create Employee"}
            </Button>
          </div>
        }
      >
        <Form id="emp-form" form={form} layout="vertical" onFinish={onFinish} requiredMark={false}>

          {/* ── Personal Information ─────────────────────────────────────── */}
          <SectionHead icon={<UserOutlined style={{ fontSize: 13, color: themeColor }} />} title="Personal Information" />

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
            <Form.Item name="firstName" rules={[{ required: true, message: "Required" }]} style={{ marginBottom: 16 }}
              label={<FieldLabel required>First Name</FieldLabel>}>
              <Input placeholder="e.g. Rahul" {...INPUT_H} />
            </Form.Item>
            <Form.Item name="lastName" rules={[{ required: true, message: "Required" }]} style={{ marginBottom: 16 }}
              label={<FieldLabel required>Last Name</FieldLabel>}>
              <Input placeholder="e.g. Sharma" {...INPUT_H} />
            </Form.Item>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
            <Form.Item name="employeeCode" rules={[{ required: true, message: "Required" }]} style={{ marginBottom: 16 }}
              label={<FieldLabel required>Employee Code</FieldLabel>}>
              <Input placeholder="e.g. EMP001" {...INPUT_H} />
            </Form.Item>
            <Form.Item name="gender" rules={[{ required: true, message: "Required" }]} style={{ marginBottom: 16 }}
              label={<FieldLabel required>Gender</FieldLabel>}>
              <Select placeholder="Select gender" options={GENDER_OPTS} allowClear {...SELECT_H} />
            </Form.Item>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
            <Form.Item name="dob" rules={[{ required: true, message: "Required" }]} style={{ marginBottom: 16 }}
              label={<FieldLabel required>Date of Birth</FieldLabel>}>
              <DatePicker format="DD-MMM-YYYY" placeholder="Select DOB" maxDate={dayjs()} style={{ width: "100%", borderRadius: 8, height: 40 }} />
            </Form.Item>
            <Form.Item name="doj" rules={[{ required: true, message: "Required" }]} style={{ marginBottom: 16 }}
              label={<FieldLabel required>Date of Joining</FieldLabel>}>
              <DatePicker format="DD-MMM-YYYY" placeholder="Select DOJ" maxDate={dayjs()} style={{ width: "100%", borderRadius: 8, height: 40 }} />
            </Form.Item>
          </div>

          {/* ── Work Details ─────────────────────────────────────────────── */}
          <SectionHead icon={<SolutionOutlined style={{ fontSize: 13, color: themeColor }} />} title="Work Details" />

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
            <Form.Item name="departmentID" rules={[{ required: true, message: "Required" }]} style={{ marginBottom: 16 }}
              label={<FieldLabel required>Department</FieldLabel>}>
              <Select placeholder="Select department" showSearch optionFilterProp="label"
                options={departments.map(d => ({ value: d.ID, label: d.DepartmentName }))} allowClear {...SELECT_H} />
            </Form.Item>
            <Form.Item name="designationID" rules={[{ required: true, message: "Required" }]} style={{ marginBottom: 16 }}
              label={<FieldLabel required>Designation</FieldLabel>}>
              <Select placeholder="Select designation" showSearch optionFilterProp="label"
                options={designations.map(d => ({ value: d.ID, label: d.DesignationName }))} allowClear {...SELECT_H} />
            </Form.Item>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
            <Form.Item name="employeeType" rules={[{ required: true, message: "Required" }]} style={{ marginBottom: 16 }}
              label={<FieldLabel required>Employee Type</FieldLabel>}>
              <Select placeholder="Select type" showSearch optionFilterProp="label"
                options={empTypes.map(e => ({ value: e.EmploymentTypeID, label: e.Description }))} allowClear {...SELECT_H} />
            </Form.Item>
            <Form.Item name="managerId" style={{ marginBottom: 16 }}
              label={<FieldLabel>Manager</FieldLabel>}>
              <Select placeholder="Select manager" showSearch optionFilterProp="label"
                options={managerOpts} allowClear {...SELECT_H} />
            </Form.Item>
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", marginBottom: 16 }}>
            <div>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#111827" }}>Active Employee</p>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: "#6b7280" }}>Toggle to enable or disable this employee</p>
            </div>
            <Form.Item name="isActive" valuePropName="checked" noStyle initialValue={false}>
              <Switch />
            </Form.Item>
          </div>

          {/* ── Address Details ──────────────────────────────────────────── */}
          <SectionHead icon={<HomeOutlined style={{ fontSize: 13, color: themeColor }} />} title="Address Details" />

          {addrLoading ? (
            <div style={{ textAlign: "center", padding: "20px 0", color: "#9ca3af", fontSize: 13 }}>Loading address…</div>
          ) : (
            <>
              <Form.Item name="address1" style={{ marginBottom: 16 }} label={<FieldLabel>Address Line 1</FieldLabel>}>
                <Input.TextArea placeholder="Street address, building…" rows={2} style={{ borderRadius: 8 }} />
              </Form.Item>
              <Form.Item name="address2" style={{ marginBottom: 16 }} label={<FieldLabel>Address Line 2</FieldLabel>}>
                <Input.TextArea placeholder="Area, landmark…" rows={2} style={{ borderRadius: 8 }} />
              </Form.Item>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                <Form.Item name="cityID" style={{ marginBottom: 16 }} label={<FieldLabel>City</FieldLabel>}>
                  <Select placeholder="Select city" showSearch optionFilterProp="label"
                    options={cities.map(c => ({ value: c.ID, label: c.CityName }))} allowClear {...SELECT_H} />
                </Form.Item>
                <Form.Item name="stateId" style={{ marginBottom: 16 }} label={<FieldLabel>State</FieldLabel>}>
                  <Select placeholder="Select state" showSearch optionFilterProp="label"
                    options={states.map(s => ({ value: s.ID, label: s.StateName }))} allowClear {...SELECT_H} />
                </Form.Item>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                <Form.Item name="countryID" style={{ marginBottom: 16 }} label={<FieldLabel>Country</FieldLabel>}>
                  <Select placeholder="Select country" showSearch optionFilterProp="label"
                    options={countries.map(c => ({ value: c.ID, label: c.CountryName }))} allowClear {...SELECT_H} />
                </Form.Item>
                <Form.Item name="pincode"
                  rules={[{ pattern: /^\d{0,6}$/, message: "Max 6 digits" }]}
                  style={{ marginBottom: 16 }}
                  label={<FieldLabel>Pincode</FieldLabel>}>
                  <Input placeholder="e.g. 400001" maxLength={6} {...INPUT_H} />
                </Form.Item>
              </div>
            </>
          )}
        </Form>
      </Drawer>
    </div>
  );
}
